import React, { useState, useEffect, useRef } from 'react';

import { t } from 'i18next';
import { Trans } from 'react-i18next';

import { Box, Typography } from '@datanomik/commons-ui';
import {
  AccountBalanceOutlined,
  InsightsOutlined,
  SyncAltOutlined,
  SecurityOutlined,
  TrendingUpOutlined,
  ReceiptLongOutlined,
  AccountBalanceWalletOutlined,
  ShowChartOutlined,
  DownloadOutlined,
  DescriptionOutlined,

  CreditScoreOutlined,
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';

import { DatanomikIsoWhite } from '@/views/common/Logo';

/* ── subtle ambient animations ── */

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const floatMedium = keyframes`
  0%, 100% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(-4px) translateX(2px); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.04; transform: scale(1); }
  50% { opacity: 0.08; transform: scale(1.05); }
`;

/* ── data ── */

const highlights = [
  { icon: AccountBalanceOutlined, labelKey: 'login_highlight_multibank' },
  { icon: InsightsOutlined, labelKey: 'login_highlight_cashflow' },
  { icon: SyncAltOutlined, labelKey: 'login_highlight_automation' },
  { icon: SecurityOutlined, labelKey: 'login_highlight_security' },
  { icon: TrendingUpOutlined, labelKey: 'login_highlight_insights' },
  { icon: ReceiptLongOutlined, labelKey: 'login_highlight_reports' },
  { icon: AccountBalanceWalletOutlined, labelKey: 'login_highlight_cash_position' },
  { icon: ShowChartOutlined, labelKey: 'login_highlight_investments' },
  { icon: DownloadOutlined, labelKey: 'login_highlight_payments' },
  { icon: DescriptionOutlined, labelKey: 'login_highlight_bank_statements' },

  { icon: CreditScoreOutlined, labelKey: 'login_highlight_debts' },
];

const VISIBLE_COUNT = 3;
const INTERVAL_MS = 4000;
const ITEM_HEIGHT = 44;
const ITEM_GAP = 8;
const STEP = ITEM_HEIGHT + ITEM_GAP;

/* ── components ── */

const HighlightItem = ({ icon: Icon, label }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      height: ITEM_HEIGHT,
      px: 1.75,
      bgcolor: 'rgba(255,255,255,0.04)',
      borderRadius: '10px',
      border: '1px solid rgba(255,255,255,0.05)',
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '7px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
    </Box>
    <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.7)', fontWeight: 500, lineHeight: 1.4 }}>
      {label}
    </Typography>
  </Box>
);

const HighlightCarousel = () => {
  const [offset, setOffset] = useState(0);
  const [animating, setAnimating] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setOffset((prev) => (prev + 1) % highlights.length);
        setAnimating(false);
      }, 400);
    }, INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  const items = [];
  for (let i = 0; i < VISIBLE_COUNT + 1; i++) {
    items.push({
      ...highlights[(offset + i) % highlights.length],
      slotIndex: i,
    });
  }

  const containerHeight = VISIBLE_COUNT * ITEM_HEIGHT + (VISIBLE_COUNT - 1) * ITEM_GAP;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        height: containerHeight,
        overflow: 'hidden',
      }}
    >
      {items.map(({ icon, labelKey, slotIndex }) => {
        const isLeaving = slotIndex === 0 && animating;
        const isEntering = slotIndex === VISIBLE_COUNT;
        const baseTop = slotIndex * STEP;
        const shiftedTop = (slotIndex - 1) * STEP;

        let opacity = 1;
        if (isLeaving) opacity = 0;
        if (isEntering && !animating) opacity = 0;

        return (
          <Box
            key={`${offset}-${slotIndex}`}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: animating ? shiftedTop : baseTop,
              opacity,
              transition: 'top 400ms ease, opacity 400ms ease',
            }}
          >
            <HighlightItem icon={icon} label={t(labelKey)} />
          </Box>
        );
      })}
    </Box>
  );
};

/* ── ambient floating orbs ── */

const AmbientOrbs = () => (
  <>
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        right: '10%',
        width: 120,
        height: 120,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,1)',
        opacity: 0.03,
        filter: 'blur(40px)',
        animation: `${pulseGlow} 8s ease-in-out infinite`,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: 80,
        height: 80,
        borderRadius: '50%',
        bgcolor: 'rgba(99, 102, 241, 1)',
        opacity: 0.06,
        filter: 'blur(30px)',
        animation: `${pulseGlow} 10s ease-in-out infinite 2s`,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        right: '25%',
        width: 60,
        height: 60,
        borderRadius: '50%',
        bgcolor: 'rgba(167, 139, 250, 1)',
        opacity: 0.04,
        filter: 'blur(25px)',
        animation: `${pulseGlow} 12s ease-in-out infinite 4s`,
        pointerEvents: 'none',
      }}
    />
  </>
);

/* ── styles ── */

const containerSx = {
  maxWidth: '600px',
  height: 'calc(100vh - 20px)',
  background: 'linear-gradient(160deg, rgba(10, 14, 40, 0.45) 0%, rgba(20, 28, 75, 0.3) 40%, rgba(30, 50, 140, 0.2) 100%), url(https://resources.datanomik.com/dashboard/images/login_bg.webp) 0 0 / 100% 100% no-repeat',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '52px',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '8px',
  boxShadow: '4px 0 24px rgba(15, 21, 53, 0.12), 1px 0 8px rgba(40, 69, 210, 0.06)',
};

/* ── main ── */

const ContentBoxNew = () => {
  const year = (new Date()).getFullYear();

  return (
    <Box sx={containerSx}>
      <AmbientOrbs />

      <Box sx={{ animation: `${floatSlow} 12s ease-in-out infinite`, position: 'relative', zIndex: 1 }}>
        <DatanomikIsoWhite style={{ marginBottom: 30 }} />
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ animation: `${floatMedium} 14s ease-in-out infinite 1s` }}>
          <Typography
            color="neutral.0"
            sx={{
              fontSize: 24,
              fontWeight: 600,
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
              mb: 1.5,
              '& strong': { fontWeight: 600 },
            }}
          >
            <Trans i18nKey="Welcome back to Business Gate">
              Welcome back to
              {' '}
              <strong>Business Gate</strong>
            </Trans>
          </Typography>

          <Box sx={{ width: 32, height: 2, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1, mb: 2 }} />

          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 450,
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            <Trans i18nKey="Understand your company's cash flow and make strategic decisions with Datanomik's Smart Treasury">
              Understand your company&apos;s cash flow and make strategic decisions with
              {' '}
              <strong>Datanomik&apos;s</strong>
              {' '}
              <span>Smart treasury</span>
            </Trans>
          </Typography>
        </Box>

        <HighlightCarousel />
      </Box>

      <Typography
        variant="body2"
        sx={{
          position: 'relative',
          zIndex: 1,
          fontWeight: 400,
          fontSize: '11px',
          lineHeight: 1.2,
          color: 'rgba(255,255,255,0.4)',
        }}
      >
        {t('© {{year}} Datanomik. All rights reserved.', { year })}
      </Typography>
    </Box>
  );
};

export default ContentBoxNew;
